import { useContext, useEffect, useState, useCallback } from "react";
import { callFunction } from "../api/callFunction";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { GlobalContext } from "../context/GlobalContext";
import { toast } from "react-toastify";
import { PlusIcon, TrashIcon, TicketIcon } from "@heroicons/react/24/outline";
import CouponForm from "./CouponForm";

const BUTTON_PRIMARY =
  "inline-flex items-center gap-x-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark";
const TABLE_HEADER =
  "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider";
const TABLE_CELL = "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100";

// Shared between the desktop table and the mobile card list.
function ActiveToggle({ coupon, onToggle }) {
  return (
    <button
      onClick={() => onToggle(coupon)}
      aria-label={coupon.active ? "Deactivate coupon" : "Activate coupon"}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        coupon.active ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          coupon.active ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function DeleteButton({ coupon, onDelete }) {
  return (
    <button
      onClick={() => onDelete(coupon)}
      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center gap-1"
    >
      <TrashIcon className="h-4 w-4" />
      Delete
    </button>
  );
}

// Standalone (super-admin nav, all coupons) or embedded on an event's edit page
// via `eventID`/`eventData`, where everything is scoped to that one event.
export default function Coupons({ eventID: scopedEventID, eventData }) {
  const embedded = !!scopedEventID;
  const { globalState } = useContext(GlobalContext);
  const [coupons, setCoupons] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterEventID, setFilterEventID] = useState("");
  const effectiveEventID = embedded ? scopedEventID : filterEventID;

  // Standalone sits on the slate page background, where a white / gray-800 card
  // plus a shadow reads as its own surface. Embedded, the parent is the accordion
  // card, which is already bg-white dark:bg-gray-800 — same colour, and Tailwind's
  // black-based shadow does nothing on gray-800, so the surfaces disappear into
  // it. The other accordion sections separate with a border instead.
  const surfaceEdge = embedded ? "border border-gray-200 dark:border-gray-700" : "shadow";

  // A coupon on a finished or called-off event is still fully redeemable — the
  // validate and redeem paths never read isCompleted or status — so a code made
  // here would be live on an event that can no longer be attended. Reading and
  // deactivating stay open; only creation closes.
  const eventClosed =
    embedded && (eventData?.isCompleted === true || eventData?.status === "cancelled");

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const payload = effectiveEventID ? { eventID: effectiveEventID } : {};
      const result = await callFunction("adminListCoupons", payload);
      setCoupons(result?.coupons || []);
    } catch (err) {
      toast.error(err.message || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [effectiveEventID]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  // Load upcoming events for the picker (form + filter dropdown).
  // Super admins see all events; everyone else is scoped to events they own.
  useEffect(() => {
    const loadEvents = async () => {
      if (embedded) {
        setEvents([{ id: scopedEventID, name: eventData?.name, cost: eventData?.cost }]);
        return;
      }
      try {
        const constraints = [where("isCompleted", "==", false)];
        if (!globalState.isSuperAdmin) {
          if (!globalState.vendorID) return;
          constraints.push(where("vendorID", "==", globalState.vendorID));
        }
        constraints.push(orderBy("date", "asc"), limit(200));
        const q = query(collection(db, "events"), ...constraints);
        const snap = await getDocs(q);
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to load events", err);
      }
    };
    loadEvents();
  }, [globalState.isSuperAdmin, globalState.vendorID, embedded, scopedEventID, eventData?.name, eventData?.cost]);

  const eventNameByID = (id) =>
    events.find((e) => e.id === id)?.name || id;

  // A coupon is single-event (eventID), multi-event (eventIDs array) or global
  // (allEvents, which names no events at all — it covers every event, including
  // ones created after it).
  const couponIsGlobal = (c) => c.allEvents === true;

  const couponEventIDs = (c) =>
    Array.isArray(c.eventIDs) && c.eventIDs.length
      ? c.eventIDs
      : c.eventID
      ? [c.eventID]
      : [];

  const otherEventCount = (c) =>
    couponEventIDs(c).filter((id) => id !== scopedEventID).length;

  // Whether a row covers more than the event it is embedded on.
  const coversOtherEvents = (c) => couponIsGlobal(c) || otherEventCount(c) > 0;

  // Embedded, every row already applies to this event, so the only new fact is
  // whether it ALSO covers others. Their names aren't loaded here, and printing
  // the raw doc ids instead is both unreadable and someone else's event id.
  const couponEventsLabel = (c) => {
    if (couponIsGlobal(c)) return "All events";
    if (embedded) {
      const others = otherEventCount(c);
      return others === 0 ? "This event only" : `+ ${others} other event${others === 1 ? "" : "s"}`;
    }
    const ids = couponEventIDs(c);
    if (ids.length === 0) return "—";
    if (ids.length === 1) return eventNameByID(ids[0]);
    return `${ids.length} events`;
  };

  // Tooltip listing every event a multi-event coupon covers.
  const couponEventsTitle = (c) => {
    if (couponIsGlobal(c)) return "Applies to every event, including ones created later";
    if (embedded) {
      const others = otherEventCount(c);
      return others === 0
        ? "Applies only to this event"
        : `Also applies to ${others} other event${others === 1 ? "" : "s"}`;
    }
    return couponEventIDs(c).map(eventNameByID).join(", ");
  };

  // Mirrors ensureCanManageCoupon on the server: a super admin manages anything;
  // a host manages a coupon owned by them, or an unowned one whose single event
  // they own. A multi-event or global coupon has neither vendorID nor eventID,
  // so a host can see one covering their event but cannot toggle or delete it —
  // showing those controls would only produce a permission-denied toast.
  const canManageCoupon = (c) =>
    globalState.isSuperAdmin || (c.vendorID ? c.vendorID === globalState.vendorID : !!c.eventID);

  const adminOnlyTitle = (c) =>
    couponIsGlobal(c)
      ? "This code works on every event, so only an admin can change it."
      : "This code spans several events, so only an admin can change it.";

  // Only worth a column when some row says something other than "this event".
  const showScopeColumn = !embedded || coupons.some(coversOtherEvents);

  const onToggleActive = async (coupon) => {
    try {
      await callFunction("adminUpdateCoupon", { couponID: coupon.id, active: !coupon.active });
      toast.success(coupon.active ? "Deactivated" : "Activated");
      fetchCoupons();
    } catch (err) {
      toast.error(err.message || "Failed to update coupon");
    }
  };

  const onDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}"? This cannot be undone.`)) return;
    try {
      await callFunction("adminDeleteCoupon", { couponID: coupon.id });
      toast.success("Deleted");
      fetchCoupons();
    } catch (err) {
      toast.error(err.message || "Failed to delete coupon");
    }
  };

  return (
    <div className={embedded ? "" : "px-4 sm:px-6 lg:px-8"}>
      <div className={`flex ${embedded ? "" : "mt-4 sm:mt-0"} justify-between items-center`}>
        {embedded ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {eventClosed
              ? "Discount codes for this event. New codes are closed because the event has ended or been cancelled."
              : "Discount codes for this event."}
          </p>
        ) : (
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <TicketIcon className="h-6 w-6 text-primary" />
            Coupons
          </h1>
        )}
        {!eventClosed && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className={`${BUTTON_PRIMARY} flex-shrink-0`}
          >
            <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
            New coupon
          </button>
        )}
      </div>

      {!embedded && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0">Filter by event:</label>
          <select
            value={filterEventID}
            onChange={(e) => setFilterEventID(e.target.value)}
            className="w-full min-w-0 sm:w-auto sm:max-w-xs rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-2 py-1 text-sm"
          >
            <option value="">All events</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}{typeof e.cost === "number" && e.cost > 0 ? ` ($${e.cost})` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className={`mt-6 rounded-md bg-white dark:bg-gray-800 ${surfaceEdge} px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400`}>
          Loading…
        </div>
      ) : coupons.length === 0 ? (
        <div className={`mt-6 rounded-md bg-white dark:bg-gray-800 ${surfaceEdge} px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400`}>
          No coupons yet.
        </div>
      ) : (
        // A coupon code is redeemable, so a live 100%-off code sitting in a
        // session replay is a giveaway. The recorder captures on-screen text by
        // default (only inputs are masked), so this list has to opt out itself.
        <div data-ph-block>
          {/* Desktop: table */}
          <div className={`hidden md:block mt-6 bg-white dark:bg-gray-800 ${surfaceEdge} overflow-hidden sm:rounded-md overflow-x-auto`}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className={TABLE_HEADER}>Code</th>
                  {showScopeColumn && (
                    <th className={TABLE_HEADER}>{embedded ? "Applies to" : "Event"}</th>
                  )}
                  <th className={TABLE_HEADER}>Type</th>
                  <th className={TABLE_HEADER}>Used / Cap</th>
                  <th className={TABLE_HEADER}>Active</th>
                  <th className={TABLE_HEADER}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {coupons.map((c) => (
                  <tr key={c.id}>
                    <td className={`${TABLE_CELL} font-mono font-semibold`}>{c.code}</td>
                    {showScopeColumn && (
                      <td className={TABLE_CELL} title={couponEventsTitle(c)}>{couponEventsLabel(c)}</td>
                    )}
                    <td className={TABLE_CELL}>
                      {c.discountType === "free" ? "Free" : `${c.percentageOff}% off`}
                    </td>
                    <td className={TABLE_CELL}>
                      {c.usedCount || 0} / {c.maxUses}
                    </td>
                    <td className={TABLE_CELL}>
                      {canManageCoupon(c) ? (
                        <ActiveToggle coupon={c} onToggle={onToggleActive} />
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {c.active ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    <td className={TABLE_CELL}>
                      {canManageCoupon(c) ? (
                        <DeleteButton coupon={c} onDelete={onDelete} />
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400" title={adminOnlyTitle(c)}>
                          Admin only
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <ul className="md:hidden mt-6 space-y-3">
            {coupons.map((c) => (
              <li key={c.id} className={`bg-white dark:bg-gray-800 ${surfaceEdge} rounded-lg p-4`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {c.code}
                    </div>
                    {(!embedded || coversOtherEvents(c)) && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate" title={couponEventsTitle(c)}>
                        {couponEventsLabel(c)}
                      </div>
                    )}
                  </div>
                  {canManageCoupon(c) ? (
                    <ActiveToggle coupon={c} onToggle={onToggleActive} />
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">
                    {c.discountType === "free" ? "Free" : `${c.percentageOff}% off`}
                    <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
                    {c.usedCount || 0} / {c.maxUses} used
                  </span>
                  {canManageCoupon(c) ? (
                    <DeleteButton coupon={c} onDelete={onDelete} />
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400" title={adminOnlyTitle(c)}>
                      Admin only
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showForm && (
        <CouponForm
          events={events}
          defaultEventID={embedded ? "" : filterEventID}
          lockedEventID={embedded ? scopedEventID : undefined}
          isSuperAdmin={globalState.isSuperAdmin}
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            fetchCoupons();
          }}
        />
      )}
    </div>
  );
}
